import { isNil } from "lodash";

import type { ColumnsType, AnyObject } from "@similarweb/pro-logic-components";

import { GroupTrafficShare, TrafficShareWithVisits } from "app/components/React/Table/cells";
import { NO_DATA_SIGN } from "app/filters/const";
import { translate } from "app/filters/i18nFilter";
import { allTrackers } from "app/services/track/track";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import { SearchOverviewTexts } from "../../texts";
import type { FormattedNumber } from "../../types";
import { renderChangeCell, renderProgressBarCell } from "../TableCells/tableCells";

import { LinkText } from "./styledComponent";

const getOrganicVsPaidColumn = (): ColumnsType<AnyObject>[0] => ({
    title: translate(SearchOverviewTexts.organicVsPaid),
    dataIndex: "percentageOf",
    width: 246,
    render: (percentageOf: { organic: FormattedNumber; paid: FormattedNumber }) => {
        if (isNil(percentageOf?.organic?.numericValue) || isNil(percentageOf?.paid?.numericValue)) {
            return NO_DATA_SIGN;
        }

        return renderProgressBarCell({
            leftValue: percentageOf.organic.formattedValue,
            rightValue: percentageOf.paid.formattedValue,
            percentageValue: percentageOf.organic.numericValue,
        });
    },
});

const getCompetitiveTrafficShareColumn = (): ColumnsType<AnyObject>[0] => ({
    title: translate("analysis.source.search.overview.competitive.traffic.share"),
    dataIndex: "sitesClickShare",
    width: 246,
    render: (sitesClickShare: Record<string, number>) =>
        sitesClickShare ? <GroupTrafficShare value={sitesClickShare} /> : NO_DATA_SIGN,
});

export const getTopNonBrandedKeywordsColumns = (
    showOrganicVsPaidColumn: boolean,
    isCompareMode: boolean,
): ColumnsType<AnyObject> => {
    const columns: ColumnsType<AnyObject> = [
        {
            title: translate("analysis.source.search.overview.search.terms"),
            dataIndex: "searchTerm",
            width: 246,
            render: (searchTerm: string) => {
                const InnerLinkPage = "organicsearch_keywordAnalysis_overview_2";
                const params = proNavigator.getParams();
                const innerLink = proNavigator.href(InnerLinkPage, { ...params, keyword: searchTerm });
                const handleKeywordClick = () => {
                    allTrackers.dispatchTrackEvent("Button", "click", `Top non-branded keywords/${searchTerm}`);
                };
                return (
                    <LinkText
                        href={innerLink}
                        onClick={handleKeywordClick}
                    >
                        {searchTerm}
                    </LinkText>
                );
            },
        },
        {
            title: translate("analysis.source.search.overview.clicks"),
            dataIndex: "clicks",
            width: 235,
            render: ({ numberOfClicks, shareOfClicks }: { numberOfClicks: number; shareOfClicks: number }) => {
                return (
                    <TrafficShareWithVisits
                        row={{ TotalVisits: numberOfClicks }}
                        value={shareOfClicks}
                        applyAbbrNumberFilter
                        thresholdForTotalFilter={0}
                        undefinedSign={NO_DATA_SIGN}
                    />
                );
            },
        },
        {
            title: translate(SearchOverviewTexts.yoyChange),
            dataIndex: "yoyChange",
            width: 150,
            align: "left",
            render: ({ value, presentation }: { value: number; presentation: string }) =>
                renderChangeCell(value, presentation, "left"),
        },
    ];
    if (!isCompareMode && showOrganicVsPaidColumn) {
        columns.push(getOrganicVsPaidColumn());
    }

    if (isCompareMode) {
        columns.push(getCompetitiveTrafficShareColumn());
    }

    return columns;
};
